import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Settings2 } from 'lucide-react';
import { Button, IconButton } from './ui';
import styles from './VoiceAudioSettings.module.css';
import { useTranslation } from '../../lib/i18n';
import { centerInline, clampInlineStart, windowViewport } from '../../lib/popover-placement';
import {
  AudioDevice,
  listAudioDevicesWithLabels,
  loadInputDevice,
  loadOutputDevice,
  loadVolume,
  outputDeviceSupported,
  saveInputDevice,
  saveOutputDevice,
  saveVolume,
} from '../../lib/voice-audio-prefs';

export interface VoiceAudioSettingsProps {
  /**
   * Current mic level, 0..1, polled while the panel is open. This is the point of the panel on a
   * call that seems deaf: without it, "it can't hear me" and "the browser picked the wrong input"
   * are the same observation, and switching devices tells you nothing about which you fixed.
   */
  inputLevel: () => number;
  /** Live setters on the running call — every change applies to the call in progress. */
  onVolume: (volume: number) => void;
  onInputDevice: (deviceId: string | null) => Promise<void>;
  onOutputDevice: (deviceId: string | null) => Promise<void>;
  /** Play a tone through the chosen speaker. */
  onTestSpeaker: () => Promise<void>;
  /**
   * Controlled open state. Left out, the panel owns it. The call surface controls it so the
   * "we can't hear you" hint can open this panel directly on the picker that fixes it.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Trigger label + icon, so the same panel can read as "audio settings" on a call and "check
   * your audio" before one. */
  triggerLabel?: string;
  triggerIcon?: React.ReactNode;
  /** Extra class on the trigger button. The voice HEADER passes its gray-surface ink-hover skin here;
   * the composer/bottom placement leaves it unset and keeps the DS ghost hover. The selected (open)
   * fill is applied inline regardless, so it shows in both placements. */
  triggerClassName?: string;
  triggerSize?: 'small' | 'medium' | 'large';
  panelAlign?: 'start' | 'center' | 'end';
  /** Which side of the trigger the panel hangs from. `above` (default) suits a trigger near the
   * bottom (the composer); `below` a trigger near the top (the call surface's top bar), where opening
   * upward would run off the top of the screen. */
  placement?: 'above' | 'below';
  /** Hang the panel's TOP off this element's bottom instead of the trigger's (placement `below` only),
   * so it lines up with a sibling popover that anchors to the same element. The voice header passes its
   * top bar so this panel drops from the same header-bottom line as the "About Voice mode" info card,
   * rather than from the settings button (which sits higher inside the header). Horizontal alignment
   * stays trigger-based. */
  blockAnchorRef?: React.RefObject<HTMLElement | null>;
  /** Gap below `blockAnchorRef`'s bottom (default 10, matching the trigger-relative gap). */
  blockAnchorGap?: number;
}

/**
 * Volume and device controls for a live call, behind one button.
 *
 * These exist because "it can't hear me" and "my mic is set to the wrong device" are
 * indistinguishable from the caller's side — without a picker there is no way to discover, let
 * alone fix, a call that is silent because the browser chose a webcam microphone. Collapsed into
 * a popover so the call surface stays a call surface rather than a mixing desk.
 *
 * Every control applies to the call in progress AND is remembered, so a device chosen once during
 * a bad call is already right on the next one.
 */
export function VoiceAudioSettings({
  inputLevel,
  onVolume,
  onInputDevice,
  onOutputDevice,
  onTestSpeaker,
  open: openProp,
  onOpenChange,
  triggerLabel,
  triggerIcon,
  triggerClassName,
  triggerSize = 'large',
  panelAlign = 'start',
  placement = 'above',
  blockAnchorRef,
  blockAnchorGap = 10,
}: VoiceAudioSettingsProps) {
  const { t } = useTranslation();
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [volume, setVolume] = useState(() => loadVolume());
  const [inputs, setInputs] = useState<AudioDevice[]>([]);
  const [outputs, setOutputs] = useState<AudioDevice[]>([]);
  const [inputId, setInputId] = useState<string | null>(() => loadInputDevice());
  const [outputId, setOutputId] = useState<string | null>(() => loadOutputDevice());
  const [switchFailed, setSwitchFailed] = useState(false);
  const [level, setLevel] = useState(0);
  const outputSupported = useRef<boolean | null>(null);
  // `anchorRef` is the inline trigger; `floatRef` is the panel, which is PORTALED to <body> (see
  // below) so it lives outside the trigger's DOM subtree — hence two refs for the outside-click.
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const floatRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number } | null>(null);

  if (outputSupported.current === null) outputSupported.current = outputDeviceSupported();

  // Device names only exist once mic permission has been granted on this origin. On a live call
  // it already has been, so this is a plain enumeration; opened BEFORE a call it may need one
  // throwaway getUserMedia to unlock the labels — which is why it runs on open and never on
  // mount (see listAudioDevicesWithLabels). Re-read on devicechange so plugging in a headset
  // shows up.
  useEffect(() => {
    if (!open) return;
    let live = true;
    const refresh = () => {
      void listAudioDevicesWithLabels().then(({ inputs: i, outputs: o, permissionDenied: denied }) => {
        if (!live) return;
        setInputs(i);
        setOutputs(o);
        setPermissionDenied(denied);
      });
    };
    refresh();
    navigator.mediaDevices?.addEventListener?.('devicechange', refresh);
    return () => {
      live = false;
      navigator.mediaDevices?.removeEventListener?.('devicechange', refresh);
    };
  }, [open]);

  // Poll the mic level while open. rAF rather than state-per-chunk: this updates at audio rate and
  // must not re-render the panel on every frame's worth of samples.
  useEffect(() => {
    if (!open) return;
    let raf = 0;
    let last = 0;
    const tick = () => {
      const next = Math.min(1, inputLevel() * 6);
      if (Math.abs(next - last) > 0.02) {
        last = next;
        setLevel(next);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, inputLevel]);

  // Anchor the portaled panel to the trigger, measured from the trigger's viewport rect. The
  // chat surface it lives in is `overflow: hidden` (FloatingChat), which CLIPS an in-flow popover
  // no matter its z-index — the whole reason the panel is portaled to <body> instead. Re-measured
  // on resize/scroll so it tracks the button. `panelAlign` picks which edge it hangs from.
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r) return;
      // Hang below a top-anchored trigger, above a bottom-anchored one — opening upward from the
      // call surface's top bar would run the panel off the top of the screen. When a block anchor is
      // given (the voice header's top bar), drop the panel from ITS bottom so it lines up with the info
      // card that hangs off the same element, instead of from the settings button (which sits higher).
      const blockAnchor = blockAnchorRef?.current;
      const belowTop = blockAnchor ? blockAnchor.getBoundingClientRect().bottom + blockAnchorGap : r.bottom + 10;
      const vertical = placement === 'below' ? { top: belowTop } : { bottom: window.innerHeight - r.top + 10 };
      const vp = windowViewport();
      // Horizontal: when centred over a block anchor (the voice header's top bar), CENTRE the panel on
      // that span — clamped so it stays inside it — exactly like the "About Voice mode" info card. This
      // is what keeps it inside the right-docked side-chat instead of clamping only to the window (which
      // let it run off the dock's LEFT edge). Otherwise fall back to trigger-relative start/center/end,
      // clamped into the window (Monday 12714162215 — a start-aligned panel near the window's right edge
      // would otherwise overflow it).
      const blockAnchorEl = blockAnchorRef?.current;
      let left: number;
      if (panelAlign === 'center' && blockAnchorEl) {
        const b = blockAnchorEl.getBoundingClientRect();
        left = centerInline({ start: b.left, end: b.right }, PANEL_WIDTH, vp, EDGE_MARGIN) - PANEL_WIDTH / 2;
      } else {
        const preferred =
          panelAlign === 'center' ? r.left + r.width / 2 - PANEL_WIDTH / 2 : panelAlign === 'end' ? r.right - PANEL_WIDTH : r.left;
        left = clampInlineStart(preferred, PANEL_WIDTH, vp, EDGE_MARGIN);
      }
      setPos({ ...vertical, left });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, panelAlign, placement, blockAnchorRef, blockAnchorGap]);

  // Close on outside click / Escape, like any popover. The panel is portaled out of the trigger's
  // subtree, so "outside" means outside BOTH the trigger and the floating panel.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!anchorRef.current?.contains(target) && !floatRef.current?.contains(target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const applyVolume = (next: number) => {
    setVolume(next);
    onVolume(next);
    saveVolume(next);
  };

  const applyInput = (deviceId: string | null) => {
    const previous = inputId;
    setInputId(deviceId);
    setSwitchFailed(false);
    void onInputDevice(deviceId)
      .then(() => saveInputDevice(deviceId))
      .catch((err: unknown) => {
        // A rolled-back switch means the caller is still on a working microphone — saying
        // something went wrong would be more alarming than the truth.
        if ((err as { rolledBack?: boolean })?.rolledBack) setInputId(previous);
        else setSwitchFailed(true);
      });
  };

  const applyOutput = (deviceId: string | null) => {
    setOutputId(deviceId);
    void onOutputDevice(deviceId).then(() => saveOutputDevice(deviceId));
  };

  const volumePct = Math.round(volume * 100);
  const panelBody = (
    <div style={SECTIONS}>
      {/* Volume */}
      <div style={SECTION}>
        <label style={LABEL} htmlFor="voice-volume">
          {t('voiceCall.volume')}
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            id="voice-volume"
            type="range"
            min={0}
            max={100}
            step={1}
            value={volumePct}
            onChange={(e) => applyVolume(Number(e.target.value) / 100)}
            className={styles.slider}
            // --slider-pct drives the WebKit track's fill gradient (see the module); the pct also fills
            // Firefox's native ::-moz-range-progress.
            style={{ ['--slider-pct' as string]: `${volumePct}%`, flex: 1 } as React.CSSProperties}
          />
          <span style={VALUE}>{volumePct}%</span>
        </div>
      </div>

      {/* Microphone */}
      <div style={SECTION}>
        <label style={LABEL} htmlFor="voice-mic">
          {t('voiceCall.microphone')}
        </label>
        {/* Moves when the mic hears you. Flat means the selected device isn't picking you up —
            which is the one thing the caller otherwise cannot tell. */}
        <div style={METER_TRACK} aria-hidden>
          <div style={{ ...METER_FILL, width: `${Math.round(level * 100)}%` }} />
        </div>
        <div style={SELECT_WRAP}>
          <select id="voice-mic" value={inputId ?? ''} onChange={(e) => applyInput(e.target.value || null)} className={styles.select} style={SELECT}>
            <option value="">{t('voiceCall.systemDefault')}</option>
            {inputs.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label}
              </option>
            ))}
          </select>
          <ChevronDown size={16} style={CHEVRON} aria-hidden />
        </div>
        {switchFailed && <p style={HINT_ERR}>{t('voiceCall.micSwitchFailed')}</p>}
        {/* Blocked permission is why the list is unnamed AND why the meter never moves — say
            so, rather than leaving the user to conclude their microphone is broken. */}
        {permissionDenied && <p style={HINT_ERR}>{t('voiceCall.micPermissionNeeded')}</p>}
      </div>

      {/* Speaker */}
      <div style={SECTION}>
        <label style={LABEL} htmlFor="voice-speaker">
          {t('voiceCall.speaker')}
        </label>
        <div style={SELECT_WRAP}>
          <select
            id="voice-speaker"
            value={outputId ?? ''}
            onChange={(e) => applyOutput(e.target.value || null)}
            disabled={!outputSupported.current}
            className={styles.select}
            style={SELECT}
          >
            <option value="">{t('voiceCall.systemDefault')}</option>
            {outputs.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label}
              </option>
            ))}
          </select>
          <ChevronDown size={16} style={CHEVRON} aria-hidden />
        </div>
        {/* Saying the browser can't do it beats a control that silently does nothing. */}
        {!outputSupported.current && <p style={HINT}>{t('voiceCall.speakerUnsupported')}</p>}
        {/* The mic has a meter; the speaker had nothing. Without this, picking an output is an
            act of faith — you only learn it was wrong when the agent seems silent. Figma "Test speaker":
            a full-width black primary button. */}
        <Button variant="contained" color="standard" size="medium" fullWidth onClick={() => void onTestSpeaker()} style={TEST_SPEAKER_BUTTON}>
          {t('voiceCall.testSpeaker')}
        </Button>
      </div>
    </div>
  );

  return (
    <div ref={anchorRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <IconButton
        size={triggerSize}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={triggerLabel ?? t('voiceCall.audioSettings')}
        title={triggerLabel ?? t('voiceCall.audioSettings')}
        // Hover skin is caller-supplied (the header passes its gray-surface ink hover; the bottom
        // placement keeps the DS ghost hover). The selected fill is inline + surface-agnostic, so the
        // panel-open state shows in both placements, matching the info button.
        className={triggerClassName}
        style={open ? { background: 'var(--ds-fill-ghost-active)' } : undefined}
      >
        {triggerIcon ?? <Settings2 size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />}
      </IconButton>

      {/* Portaled to <body> so the FloatingChat's `overflow: hidden` can't clip it; positioned
          from the trigger's viewport rect (see the measure effect). */}
      {open &&
        pos &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={floatRef}
            role="dialog"
            aria-label={triggerLabel ?? t('voiceCall.audioSettings')}
            style={{
              ...PANEL,
              position: 'fixed',
              top: pos.top ?? 'auto',
              left: pos.left,
              bottom: pos.bottom ?? 'auto',
            }}
          >
            {panelBody}
          </div>,
          document.body,
        )}
    </div>
  );
}


// Figma "Symphony Card Box" 14701-107215 is 280 wide. Cap there, but shrink to fit narrower surfaces
// (mobile-web, side-chat) so the panel never overflows and keeps a margin off the chat-content edges —
// the width is min(280, viewport − 2·EDGE_MARGIN), and the placement clamps it EDGE_MARGIN off the edges.
const PANEL_WIDTH = 280;
const EDGE_MARGIN = 16;

// The visual box only. Positioning (fixed, above/below the trigger, edge-aligned) is applied at the call
// site from the measured anchor rect, because the panel is portaled to <body> — see the render.
const PANEL: React.CSSProperties = {
  width: `min(${PANEL_WIDTH}px, calc(100vw - ${EDGE_MARGIN * 2}px))`,
  boxSizing: 'border-box',
  padding: '12px 16px',
  borderRadius: 'var(--sym-radius-card)',
  background: 'var(--sym-bg-elevated)',
  boxShadow: '0 20px 30px rgba(var(--ds-neutral-950-rgb), 0.1), 0 0 2px rgba(var(--ds-neutral-950-rgb), 0.1)',
  textAlign: 'left',
  // Portaled to <body>, so it must clear the FloatingChat dock (z-index 50). Matches the chat's
  // other body-level overlays (the contact/place/time pickers at 60).
  zIndex: 60,
};

const SECTIONS: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 12, width: '100%' };
const SECTION: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8, width: '100%' };

const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: '-0.28px',
  color: 'var(--sym-ink)',
  margin: 0,
};

const SELECT_WRAP: React.CSSProperties = { position: 'relative', width: '100%' };

// Device picker — Figma "Symphony Input": 36 tall, 1px #969696 border, 8 radius, 12px inline padding,
// value in "Small/Medium" secondary ink. `appearance:none` (module) drops the native arrow; the Lucide
// chevron below sits in the reserved right padding.
const SELECT: React.CSSProperties = {
  width: '100%',
  height: 36,
  boxSizing: 'border-box',
  padding: '8px 34px 8px 12px',
  borderRadius: 'var(--ds-radius-8)',
  border: '1px solid var(--ds-border-default)',
  background: 'transparent',
  color: 'var(--ds-text-secondary)',
  fontSize: 14,
  fontWeight: 'var(--sym-weight-medium)',
  lineHeight: '18px',
  letterSpacing: '-0.28px',
};

const CHEVRON: React.CSSProperties = {
  position: 'absolute',
  right: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  color: 'var(--sym-ink)',
};

const METER_TRACK: React.CSSProperties = {
  width: '100%',
  height: 8,
  borderRadius: 4,
  background: 'var(--ds-surface-light-gray)',
  overflow: 'hidden',
};

const METER_FILL: React.CSSProperties = {
  height: '100%',
  borderRadius: 4,
  background: 'var(--ds-control-fill-on-default)',
  transition: 'width 80ms linear',
};

const VALUE: React.CSSProperties = {
  fontSize: 12,
  lineHeight: '16px',
  letterSpacing: '-0.36px',
  color: 'var(--ds-text-secondary)',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  minWidth: 30,
};
const HINT: React.CSSProperties = { fontSize: 12, lineHeight: '16px', letterSpacing: '-0.36px', color: 'var(--ds-text-secondary)', margin: 0 };
const HINT_ERR: React.CSSProperties = { ...HINT, color: 'var(--ds-text-destructive)' };
const TEST_SPEAKER_BUTTON: React.CSSProperties = {
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
};
